import React, {
 useCallback,
 useEffect,
 useMemo,
 useState,
} from 'react'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'helpers'
import KTTable from 'helpers/ui-components/KTTable'
import {ColumnProps} from 'models/Common'
import {IInfra} from 'models/MyAccount'
import Spinner from 'layout/common/Spinner'
import {KTConfirmPopup} from 'helpers/ui-components/KTConfirmPopup'
import {
 createInfra,
 deleteInfra,
 fetchInfraList,
 updateInfra,
} from 'utils/api/auth/myAccountService'
import AddInfraModal, {
 IInfraFormPayload,
} from './AddInfraModal'

type IProps = {
 userId?: string
}

const InfraSection = (props: IProps) => {
 const {t} = useTranslation()
 const [loader, setLoader] = useState(false)
 const [infraList, setInfraList] = useState<IInfra[]>([])
 const [showInfraModal, setShowInfraModal] = useState(false)
 const [editInfra, setEditInfra] = useState<IInfra | null>(
  null
 )
 const [deleteInfraItem, setDeleteInfraItem] =
  useState<IInfra | null>(null)

 const loadInfraList = useCallback(async () => {
  setLoader(true)
  const list = await fetchInfraList(props.userId)
  setInfraList(list ?? [])
  setLoader(false)
 }, [props.userId])

 useEffect(() => {
  void loadInfraList()
 }, [loadInfraList])

 const handleOpenAdd = () => {
  setEditInfra(null)
  setShowInfraModal(true)
 }

 const handleOpenEdit = (infra: IInfra) => {
  setEditInfra(infra)
  setShowInfraModal(true)
 }

 const handleCloseModal = () => {
  setShowInfraModal(false)
  setEditInfra(null)
 }

 const handleSaveInfra = async (
  values: IInfraFormPayload
 ) => {
  setLoader(true)
  const payload: IInfra = {
   infra_type: values.infra_type,
   state: values.state,
   district: values.district,
   pincode: values.pincode,
   address: values.address,
   is_active: values.is_active ?? true,
   ...(values.id && {id: values.id}),
   ...(values.gst_no && {gst_no: values.gst_no}),
   ...(values.gst_certificate && {
    gst_certificate: values.gst_certificate,
   }),
  }

  const res = values.id
   ? await updateInfra(payload, props.userId)
   : await createInfra(payload, props.userId)

  setLoader(false)

  if (res) {
   handleCloseModal()
   await loadInfraList()
  }
 }

 const handleDeleteConfirm = async (isConfirm: boolean) => {
  if (isConfirm && deleteInfraItem?.id) {
   setLoader(true)
   const res = await deleteInfra({
    ...(props.userId && {user_id: Number(props.userId)}),
    infra_ids: [deleteInfraItem.id],
   })
   setLoader(false)
   if (res) {
    await loadInfraList()
   }
  }
  setDeleteInfraItem(null)
 }

 const columns: Array<ColumnProps<IInfra>> = useMemo(
  () => [
   {
    key: 'infraType',
    title: t(
     'myAccountPage.businessProfile.offtaker.infra.infraType'
    ),
    render: (_col, item) => (
     <span className='font-medium truncate max-w-[160px] block'>
      {item.infra_type}
     </span>
    ),
   },
   {
    key: 'stateName',
    title: t(
     'myAccountPage.businessProfile.offtaker.infra.stateName'
    ),
    render: (_col, item) => item.state_name || '-',
   },
   {
    key: 'district',
    title: t(
     'myAccountPage.businessProfile.offtaker.infra.district'
    ),
    render: (_col, item) => item.district_name || '-',
   },
   {
    key: 'pincode',
    title: t(
     'myAccountPage.businessProfile.offtaker.infra.pincode'
    ),
    render: (_col, item) => item.pincode || '-',
   },
   {
    key: 'gstCertificate',
    title: t(
     'myAccountPage.businessProfile.offtaker.infra.gstCertificate'
    ),
    render: (_col, item) => {
     if (!item.gst_no && !item.gst_certificate) {
      return '-'
     }
     return (
      <div className='flex items-center gap-2 min-w-0'>
       {item.gst_no && (
        <span className='text-sm text-[#33393D] truncate max-w-[120px]'>
         {item.gst_no}
        </span>
       )}
       {item.gst_certificate && (
        <a
         href={item.gst_certificate}
         target='_blank'
         rel='noreferrer'
         className='text-[#4E83E4] underline text-sm shrink-0'
         onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          window.open(
           item.gst_certificate,
           '_blank',
           'noopener,noreferrer'
          )
         }}
        >
         {t(
          'myAccountPage.businessProfile.offtaker.infra.uploadedDocument'
         )}
        </a>
       )}
      </div>
     )
    },
   },
   {
    key: 'action',
    title: t(
     'myAccountPage.businessProfile.offtaker.infra.action'
    ),
    render: (_col, item) => (
     <div className='flex items-center gap-3'>
      <KTSVG
       path='/media/icons/pencil.svg'
       svgClassName='w-4 h-4 cursor-pointer'
       onClick={() => handleOpenEdit(item)}
      />
      <span
       className='fa fa-trash text-lg cursor-pointer text-gray-700'
       onClick={() => setDeleteInfraItem(item)}
      />
     </div>
    ),
   },
  ],
  [t]
 )

 return (
  <>
   {loader && <Spinner />}
   {deleteInfraItem && (
    <KTConfirmPopup
     showConfirmModel={!!deleteInfraItem}
     handleConfirmBtnClick={handleDeleteConfirm}
     yesBtnText={t('common.yes')}
     noBtnText={t('common.no')}
     title={t('common.confirmModalTitle')}
     subTitle='Are you sure you want to delete this infrastructure?'
    />
   )}
   <div className='w-full'>
    <div className='text-[#33393D] text-lg font-bold mb-4'>
     {t(
      'myAccountPage.businessProfile.offtaker.infra.title'
     )}
    </div>
    <KTTable
     data={infraList}
     columns={columns}
     headerClass='bg-[#80868C]'
    />
    <div className='-mt-2 border-[#E6EBF0] border-[0.5px] py-3 px-4 flex items-center w-full'>
     <div
      className='flex text-[#4E83E4] text-lg font-bold items-center cursor-pointer gap-1'
      onClick={handleOpenAdd}
     >
      <KTSVG
       path='/media/icons/plus.svg'
       svgClassName='w-4 h-4'
      />
      <div>
       {t(
        'myAccountPage.businessProfile.offtaker.infra.addInfra'
       )}
      </div>
     </div>
    </div>
    <AddInfraModal
     show={showInfraModal}
     editInfra={editInfra}
     onClose={handleCloseModal}
     onSave={(values) => {
      void handleSaveInfra(values)
     }}
    />
   </div>
  </>
 )
}

export default InfraSection
